import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useOidc } from 'react-oidc-context';
import { API_BASE_IOTHUB } from '../constants/api';

const AuthContext = createContext(null);

// Zwei Token, zwei Zwecke — und das bleibt so:
//
//   iotHubToken  Keycloak-Zugriffstoken. Damit spricht die App den BFF an.
//   authToken    ThingsBoard-JWT. Die App ruft ThingsBoard an vielen Stellen
//                direkt auf, und dessen REST-API akzeptiert keine fremden
//                OIDC-Token. Der BFF gibt es unter /auth/thingsboard-token
//                heraus — ausschliesslich fuer die angemeldete Person selbst.
//
// Nach aussen sieht der Kontext aus wie vorher: Die uebrigen Komponenten holen
// sich weiterhin authToken bzw. iotHubToken und merken von der Umstellung
// nichts. Weggefallen ist handleLoginSuccess — es gibt keine eigene
// Anmeldemaske mehr, die etwas zurueckmelden koennte.

// Fehlercodes des BFF, die keine Stoerung sind, sondern eine Entscheidung:
// Die Tuer ist mit Absicht zu, und Warten oder Neuladen aendert daran nichts.
// Der Code kommt im Antwortrumpf neben "detail" (core/api_responses.py) —
// darauf laesst sich verzweigen, auf deutschen Text nicht.
const ABSICHTLICH_ZU = new Set([
  'AUTH_EMAIL_NOT_VERIFIED',  // Adresse noch nicht bestaetigt
  'ORG_EXISTING_USER',        // Adresse gehoert schon zu einem fremden Tenant
  'ORG_NAME_TAKEN',           // eingetragener Organisationsname vergeben
  'ORG_NAME_DERIVED_TAKEN',   // kein Name eingetragen, der eigene ist vergeben
]);

const EINRICHTUNG_OFFEN = { status: 'pending', code: '', message: '' };

const ANFRAGE_ID_HEADER = 'X-Request-Id';

// Eine ID je Aufruf, damit die Spur schon hier beginnt und nicht erst im BFF.
// Wer "das ging nicht" meldet, liefert damit eine ID statt einer Uhrzeit:
//   kubectl logs -n bff-v2 -l app=bff-v2 --prefix --tail=-1 | grep <id>
// Nur an den BFF: ThingsBoard kennt den Header nicht, und ein unbekannter
// Header im CORS-Preflight liesse dessen Aufrufe scheitern.
function mitAnfrageId(input, init = {}) {
  const url = typeof input === 'string' ? input : input?.url || '';
  if (API_BASE_IOTHUB ? !url.startsWith(API_BASE_IOTHUB) : !url.startsWith('/api/')) {
    return [input, init];
  }
  const id = (crypto.randomUUID?.() || String(Math.random()).slice(2)).replace(/-/g, '').slice(0, 12);
  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  if (!headers.has(ANFRAGE_ID_HEADER)) headers.set(ANFRAGE_ID_HEADER, id);
  return [input, { ...init, headers }];
}

// Eine abgelehnte Antwort des BFF, mitsamt ihrem stabilen Code. Als eigene
// Fehlerklasse, damit der Code den Weg durch das throw uebersteht — der Text
// allein sagt nicht, ob ein zweiter Anlauf ueberhaupt Sinn hat.
class BffFehler extends Error {
  constructor(body, ersatztext) {
    super(body?.detail || ersatztext);
    this.code = body?.code || '';
  }
}

export function AuthProvider({ children }) {
  const oidc = useOidc();
  const [authToken, setAuthToken] = useState('');

  // Wie weit die Einrichtung nach der Anmeldung gekommen ist:
  //
  //   pending  laeuft noch — niemand darf schon hinein
  //   ready    Organisation steht, ThingsBoard-Token liegt vor
  //   blocked  der BFF hat mit Absicht abgelehnt (siehe ABSICHTLICH_ZU)
  //   error    etwas ist schiefgegangen; ein zweiter Anlauf kann helfen
  //
  // Frueher war das ein blosser Fehlertext. Der reichte nicht: Wer angemeldet
  // war, kam in die Oberflaeche, ganz gleich wie die Einrichtung ausging, und
  // sah dort nur Seiten, die ewig "laden…" anzeigten.
  const [setup, setSetup] = useState(EINRICHTUNG_OFFEN);
  const [versuch, setVersuch] = useState(0);

  const iotHubToken = oidc.user?.access_token ?? '';

  // Bewusst allein an Keycloak festgemacht, nicht zusaetzlich am
  // ThingsBoard-Token: Sonst schickte ProtectedRoute eine angemeldete Person
  // zurueck auf /login, das sich wegen der bestehenden Sitzung sofort wieder
  // nach /home schickte — eine Schleife statt einer Fehlermeldung.
  const isLoggedIn = oidc.isAuthenticated;

  // Nur der Anmeldevorgang selbst. Die Einrichtung steckt bewusst NICHT mehr
  // darin: App.js blendet waehrend isAuthLoading die ganze Oberflaeche aus, und
  // dann koennte die Loginseite nicht sagen, worauf gerade gewartet wird.
  const isAuthLoading = oidc.isLoading;

  // Die einzige Bedingung, unter der jemand die Oberflaeche sehen darf.
  const isReady = isLoggedIn && setup.status === 'ready';

  const handleLogout = useCallback(() => {
    setAuthToken('');
    setSetup(EINRICHTUNG_OFFEN);
    localStorage.removeItem('username');
    // Merker fuer die Loginseite: Nach dem Abmelden landet man wieder dort,
    // und sie wuerde von sich aus sofort zu Keycloak weiterleiten. Wer sich
    // gerade abgemeldet hat, will aber nicht im selben Atemzug wieder vor der
    // Anmeldemaske stehen.
    localStorage.setItem('abgemeldet', '1');
    // Faellt signoutRedirect aus - kein Eintrag im oidc-Speicher, Keycloak
    // nicht erreichbar -, bliebe man sonst angemeldet auf der Seite stehen,
    // auf der man gerade "Abmelden" geklickt hat. Der Notausgang raeumt den
    // Speicher selbst und kommt ohne gueltigen Zustand aus.
    oidc.signoutRedirect().catch(() => window.location.assign('/logout'));
  }, [oidc]);

  const handleSessionExpired = useCallback(() => {
    localStorage.setItem('sessionExpiredMessage', 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
    setAuthToken('');
    setSetup(EINRICHTUNG_OFFEN);
    localStorage.removeItem('username');
    oidc.removeUser();
  }, [oidc]);

  // Stabile Referenz, damit der fetch-Aufsatz immer die aktuelle Fassung ruft
  const handleSessionExpiredRef = useRef(handleSessionExpired);
  useEffect(() => {
    handleSessionExpiredRef.current = handleSessionExpired;
  }, [handleSessionExpired]);

  // Stabile Referenz aus demselben Grund wie oben
  const oidcRef = useRef(oidc);
  useEffect(() => {
    oidcRef.current = oidc;
  }, [oidc]);

  // Globaler fetch-Aufsatz: Ein abgelaufenes BFF-Token wird einmal still
  // erneuert und die Anfrage wiederholt; erst wenn das scheitert, wird
  // abgemeldet. Dazu erneuern, wenn der BFF meldet, dass das Token veraltet ist.
  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    // Dieselbe Anfrage noch einmal, mit frischem Token. Nur wenn der Aufrufer
    // sein Token als Bearer-Header mitgab - dann ist es das, was abgelaufen ist.
    const mitNeuemToken = ([input, init = {}], token) => {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      if (!/^Bearer /i.test(headers.get('Authorization') || '')) return null;
      headers.set('Authorization', `Bearer ${token}`);
      return [input, { ...init, headers }];
    };

    window.fetch = async (...args) => {
      args = mitAnfrageId(...args);
      let response = await originalFetch(...args);
      if (response.status === 401) {
        let abgelaufen = false;
        let vomBff = false;
        try {
          const body = await response.clone().json();
          // Zwei Formen, zwei Systeme: der BFF sendet code (api_responses.py),
          // ThingsBoard message. Nur auf message zu prüfen liess jeden
          // abgelaufenen BFF-Token stumm durchgehen.
          vomBff = body?.code === 'AUTH_TOKEN_EXPIRED';
          abgelaufen =
            vomBff || (typeof body?.message === 'string' && body.message.toLowerCase().includes('expired'));
        } catch {
          // JSON-Parsefehler ignorieren
        }
        // Meist ist nur das Access-Token abgelaufen, die Sitzung lebt noch:
        // still erneuern und wiederholen. Vorher endete jeder erste Aufruf nach
        // einer Pause - typisch der Etikett-Scan - auf der Anmeldeseite.
        if (vomBff) {
          let frisch = null;
          try {
            frisch = await oidcRef.current.signinSilent();
          } catch {
            frisch = null;
          }
          const wiederholung = frisch?.access_token ? mitNeuemToken(args, frisch.access_token) : null;
          if (wiederholung) {
            response = await originalFetch(...wiederholung);
            abgelaufen = response.status === 401;
          }
        }
        if (abgelaufen) handleSessionExpiredRef.current();
      }
      // Der BFF hat die Organisation in Keycloak geaendert — etwa als er beim
      // ersten Geraet die Application "default" angelegt hat. Das vorliegende
      // Token kennt sie noch nicht; ohne Erneuerung faende die Geraeteliste das
      // eben angelegte Geraet nicht. Hier zentral, damit nicht jeder Aufrufer
      // selbst daran denken muss.
      if (response.headers.get('X-Token-Refresh-Required')) {
        try {
          await oidcRef.current.signinSilent();
        } catch {
          // Nicht schlimm: Das naechste turnusmaessige Erneuern holt es nach.
        }
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Nach der Anmeldung: Organisation sicherstellen, dann das ThingsBoard-Token
  // holen. Beides genau einmal je Sitzung.
  const eingerichtetFuer = useRef('');
  useEffect(() => {
    if (!oidc.isAuthenticated || !iotHubToken) return;
    if (eingerichtetFuer.current === iotHubToken) return;
    eingerichtetFuer.current = iotHubToken;

    let abgebrochen = false;

    const einrichten = async () => {
      // Laeuft auch nach jeder Token-Erneuerung. Wer schon eingerichtet ist,
      // bleibt es waehrenddessen: Sonst wuerde ProtectedRoute fuer den Moment
      // auf /login werfen und die Seite, auf der man gerade war, ginge verloren.
      setSetup((bisher) => (bisher.status === 'ready' ? bisher : EINRICHTUNG_OFFEN));
      try {
        // /provision ist absichtlich immer der erste Schritt: Es meldet
        // unveraendert zurueck, wenn die Organisation schon besteht. Das erspart
        // uns, im Token nach dem organization-Claim zu suchen und dessen Form zu
        // kennen.
        const provRes = await fetch(`${API_BASE_IOTHUB}/api/v1/auth/provision`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${iotHubToken}` },
        });
        if (!provRes.ok) {
          const body = await provRes.json().catch(() => ({}));
          throw new BffFehler(body, 'Die Organisation konnte nicht eingerichtet werden.');
        }

        // Die Tenant-IDs stehen erst nach einem neuen Token im organization-Claim.
        const prov = await provRes.json();
        let token = iotHubToken;
        if (prov?.token_refresh_required) {
          const erneuert = await oidc.signinSilent();
          if (!erneuert?.access_token) {
            throw new Error(
              'Die Organisation wurde angelegt, das Token liess sich aber nicht erneuern. ' +
              'Bitte neu anmelden.'
            );
          }
          token = erneuert.access_token;
        }

        const tbRes = await fetch(`${API_BASE_IOTHUB}/api/v1/auth/thingsboard-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!tbRes.ok) {
          const body = await tbRes.json().catch(() => ({}));
          throw new BffFehler(body, 'Das ThingsBoard-Token konnte nicht geholt werden.');
        }
        const tb = await tbRes.json();
        if (abgebrochen) return;
        setAuthToken(tb.token || '');
        setSetup({ status: 'ready', code: '', message: '' });
      } catch (err) {
        if (abgebrochen) return;
        // Neu versuchen erlauben, sonst bliebe der Fehler bis zum Neuladen
        eingerichtetFuer.current = '';
        setSetup({
          status: ABSICHTLICH_ZU.has(err.code) ? 'blocked' : 'error',
          code: err.code || '',
          message: err.message || String(err),
        });
      }
    };

    einrichten();
    return () => {
      abgebrochen = true;
    };
  }, [oidc, oidc.isAuthenticated, iotHubToken, versuch]);

  // Zweiter Anlauf. Zuerst ein frisches Token holen, denn genau darin steckt
  // der haeufigste Grund: "email_verified" steht im Zugriffstoken, und wer
  // gerade seine Adresse bestaetigt hat, traegt immer noch das alte mit sich —
  // ein blosses Neuladen der Seite fragte damit dasselbe noch einmal und
  // bekaeme dieselbe Absage.
  const erneutVersuchen = useCallback(async () => {
    setSetup(EINRICHTUNG_OFFEN);
    eingerichtetFuer.current = '';
    try {
      await oidc.signinSilent();
    } catch {
      // Nicht schlimm: Dann laeuft die Einrichtung eben mit dem vorhandenen
      // Token noch einmal und meldet, was weiterhin fehlt.
    }
    // Falls das Token unveraendert zurueckkam, weckt erst dieser Zaehler den
    // Effekt wieder auf.
    setVersuch((n) => n + 1);
  }, [oidc]);

  useEffect(() => {
    const email = oidc.user?.profile?.email;
    if (email) localStorage.setItem('username', email);
  }, [oidc.user]);

  // Stabile Referenz: Die Loginseite ruft das in einem Effekt und wuerde bei
  // einer bei jedem Rendern neuen Funktion immer wieder anlaufen.
  const signIn = useCallback(() => oidc.signinRedirect(), [oidc]);

  const value = {
    authToken,
    iotHubToken,
    isLoggedIn,
    isAuthLoading,
    // Angemeldet UND eingerichtet. Alles hinter ProtectedRoute haengt daran.
    isReady,
    setup,
    // Fehler aus dem Anmeldevorgang selbst (abgelehnter Code, falscher
    // Redirect, Keycloak nicht erreichbar). Getrennt von setup, das erst
    // NACH geglueckter Anmeldung entsteht — und getrennt gebraucht: Solange
    // hier etwas steht, darf die Loginseite nicht selbsttaetig weiterleiten,
    // sonst laeuft der Fehler in einer Schleife.
    authError: oidc.error?.message || '',
    handleLogout,
    handleSessionExpired,
    erneutVersuchen,
    signIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
